import sellerApi from '@/api/SellerApi'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DEFAULT_SPACE_LIST_SETTINGS,
  SpaceListColumnSettings,
  SpaceListViewSettingsSchema,
  TSpaceListViewSettings,
} from '@valuecase/common'
import { useNotifications } from '@valuecase/ui-components'
import { useCallback, useMemo } from 'react'

const SPACE_LIST_VIEWS_QUERY_KEY = 'spaceListViews'

export const useSpaceListViews = () => {
  const queryClient = useQueryClient()
  const { success } = useNotifications()

  const {
    data: views,
    isLoading,
    error,
  } = useQuery<TSpaceListViewSettings[]>({
    queryKey: [SPACE_LIST_VIEWS_QUERY_KEY],
    queryFn: async () => {
      const response = await sellerApi.get('/space-list-view')
      return response.data.map((view: unknown) => SpaceListViewSettingsSchema.parse(view))
    },
  })

  const { mutateAsync: createView } = useMutation({
    mutationFn: async (settings: Partial<TSpaceListViewSettings['settings']>) => {
      const response = await sellerApi.post('/space-list-view', settings)
      return SpaceListViewSettingsSchema.parse(response.data)
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: [SPACE_LIST_VIEWS_QUERY_KEY] })
    },
  })

  const { mutateAsync: duplicateView } = useMutation({
    mutationFn: async (viewId: string) => {
      const response = await sellerApi.post(`/space-list-view/${viewId}/duplicate`)
      return SpaceListViewSettingsSchema.parse(response.data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SPACE_LIST_VIEWS_QUERY_KEY] })
      success('View duplicated')
    },
  })

  const { mutateAsync: updateView } = useMutation({
    mutationFn: async ({
      viewId,
      settings,
    }: {
      viewId: string
      settings: Partial<SpaceListColumnSettings>
    }) => {
      const response = await sellerApi.put(`/space-list-view/${viewId}`, settings)
      return SpaceListViewSettingsSchema.parse(response.data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SPACE_LIST_VIEWS_QUERY_KEY] })
      success('View updated')
    },
  })

  const { mutateAsync: setDefaultView } = useMutation({
    mutationFn: async (viewId: string) => {
      const response = await sellerApi.post(`/space-list-view/${viewId}/set-default`)
      return SpaceListViewSettingsSchema.parse(response.data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SPACE_LIST_VIEWS_QUERY_KEY] })
      success('Default view updated')
    },
  })

  const { mutateAsync: deleteView } = useMutation({
    mutationFn: async (viewId: string) => {
      await sellerApi.delete(`/space-list-view/${viewId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SPACE_LIST_VIEWS_QUERY_KEY] })
      success('View deleted')
    },
  })

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [SPACE_LIST_VIEWS_QUERY_KEY] })
  }, [queryClient])

  const placeholderView: TSpaceListViewSettings = useMemo(() => {
    return {
      settings: DEFAULT_SPACE_LIST_SETTINGS,
      id: '',
      name: 'My Spaces',
      emoji: '📋',
      tenantId: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      userId: '',
      viewType: 'SPACE_LIST',
    }
  }, [])

  return {
    views,
    placeholderView,
    isLoading,
    error: error instanceof Error ? error : new Error('Failed to fetch views'),
    createView,
    duplicateView,
    updateView,
    setDefaultView,
    deleteView,
    refetch,
  }
}
